import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { getImageUrl } from '../utils/apiUrl'
import apiService from '../services/api'

const BannerSlider = () => {
  const [banners, setBanners] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchBanners()
  }, [])

  const fetchBanners = async () => {
    try {
      const response = await apiService.get('/banners/published')
      setBanners(response.data)
      setIsLoading(false)
    } catch (error) {
      console.error('Erreur chargement bannières:', error)
      setIsLoading(false)
    }
  }

  // Auto-rotation des bannières
  useEffect(() => {
    if (banners.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      )
    }, 5000) // Change toutes les 5 secondes

    return () => clearInterval(interval)
  }, [banners.length])

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? banners.length - 1 : currentIndex - 1)
  }

  const goToNext = () => {
    setCurrentIndex(currentIndex === banners.length - 1 ? 0 : currentIndex + 1)
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  if (isLoading) {
    return (
      <div className="relative h-96 bg-gray-200 animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-gray-500">Chargement des bannières...</div>
        </div>
      </div>
    )
  }

  if (banners.length === 0) {
    return null // Pas de bannières à afficher
  }

  return (
    <div className="relative h-[500px] overflow-hidden shadow-lg w-full">
      {/* Bannières */}
      <div 
        className="flex transition-transform duration-500 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className="w-full h-full flex-shrink-0 relative"
          >
            {/* Image de fond */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${getImageUrl(banner.image_path)})`
              }}
            >
              {/* Overlay sombre pour la lisibilité */}
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>

            {/* Contenu de la bannière */}
            <div className="relative z-10 h-full flex items-center">
              <div className="container mx-auto px-4">
                <div className="max-w-2xl text-white">
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                    {banner.title}
                  </h1>
                  
                  {banner.subtitle && (
                    <p className="text-lg md:text-xl mb-6 text-gray-200">
                      {banner.subtitle}
                    </p>
                  )}

                  {banner.button_text && banner.button_link && (
                    <Link
                      to={banner.button_link}
                      className="inline-flex items-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors transform hover:scale-105"
                    >
                      {banner.button_text}
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Boutons de navigation */}
      {banners.length > 1 && (
        <>
          {/* Bouton précédent */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
            aria-label="Bannière précédente"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Bouton suivant */}
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
            aria-label="Bannière suivante"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Indicateurs de pagination */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white' 
                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                }`}
                aria-label={`Aller à la bannière ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default BannerSlider
